import React, { FC, useEffect } from "react"
import { useAuth } from "react-oidc-context"
import { useAppDispatch } from "@app/hooks"
import { setAuth } from "@app/store"
import { App } from "@app/interfaces"
import { getUserRole } from "../../../utils"
import { CircularProgress, Box, Typography } from "@mui/material"
import { useNavigate } from "react-router-dom"

interface IOIDCCallbackProps {}

export const OIDCCallback: FC<IOIDCCallbackProps> = () => {
  const auth = useAuth()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      try {
        // Декодируем JWT токен для получения ролей
        const tokenPayload = JSON.parse(
          atob(auth.user.access_token.split(".")[1]),
        )

        // Извлекаем роли из токена (ключевые места где могут быть роли)
        let roleFromToken: string[] = []

        if (tokenPayload.roles && Array.isArray(tokenPayload.roles)) {
          roleFromToken = tokenPayload.roles
        } else if (
          tokenPayload.realm_access?.roles &&
          Array.isArray(tokenPayload.realm_access.roles)
        ) {
          roleFromToken = tokenPayload.realm_access.roles
        } else if (
          tokenPayload.resource_access &&
          typeof tokenPayload.resource_access === "object"
        ) {
          // Поиск ролей в resource_access
          for (const [resource, access] of Object.entries(
            tokenPayload.resource_access,
          )) {
            if (
              access &&
              typeof access === "object" &&
              "roles" in access &&
              Array.isArray(access.roles)
            ) {
              roleFromToken = [...roleFromToken, ...access.roles]
            }
          }
        }

        // Безопасное извлечение данных из профиля
        const profile = auth.user.profile as any
        const username = profile?.preferred_username || profile?.sub || ""

        // Определяем роль пользователя на основе Keycloak ролей
        let userRole = App.UserRole.USER
        if (roleFromToken.includes("admin")) {
          userRole = App.UserRole.ADMIN
        } else if (
          roleFromToken.includes("manager") ||
          roleFromToken.includes("Operator")
        ) {
          userRole = App.UserRole.MANAGER
        }

        // Создаем OIDC auth state
        const authState: App.AuthState = {
          type: App.AuthType.OIDC,
          token: auth.user.access_token,
          refreshToken: auth.user.refresh_token,
          idToken: auth.user.id_token,
          role: userRole,
          expiresAt: auth.user.expires_at,
          userInfo: {
            sub: profile?.sub || "",
            preferred_username: username,
            email: profile?.email,
            given_name: profile?.given_name,
            family_name: profile?.family_name,
            roles: roleFromToken,
            realm_access: tokenPayload.realm_access?.roles
              ? { roles: tokenPayload.realm_access.roles }
              : undefined,
            resource_access: tokenPayload.resource_access as
              | { [key: string]: { roles: string[] } }
              | undefined,
          },
        }

        dispatch(setAuth(authState))

        // Redirect to home page
        navigate("/", { replace: true })
      } catch (error) {
        console.error("Error processing OIDC callback:", error)
        navigate("/login", { replace: true })
      }
    } else if (auth.error) {
      console.error("OIDC authentication error:", auth.error)
      console.error("Error details:", {
        message: auth.error.message,
        name: auth.error.name,
        stack: auth.error.stack,
      })
      navigate("/login", { replace: true })
    }
  }, [auth.isAuthenticated, auth.user, auth.error, dispatch, navigate])

  if (auth.isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="50vh"
        gap={2}
      >
        <CircularProgress />
        <Typography variant="body1">Завершение авторизации...</Typography>
      </Box>
    )
  }

  if (auth.error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="50vh"
        gap={2}
      >
        <Typography variant="h6" color="error">
          Ошибка авторизации
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {auth.error.message}
        </Typography>
      </Box>
    )
  }

  return null
}
